import { getAccessToken } from "../utils/auth";
import React, { useEffect, useState } from "react";
import axios from "../utils/api.js";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Box,
  IconButton,
  CircularProgress,
  Button,
  ButtonGroup,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const PaymentStatistics = () => {
  const [statistics, setStatistics] = useState(null);
  const [allStatistics, setAllStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState("month");
  const [viewMode, setViewMode] = useState("single"); // "single" or "all"
  const navigate = useNavigate();

  useEffect(() => {
    if (viewMode === "single") {
      fetchStatistics(selectedPeriod);
    } else {
      fetchAllStatistics();
    }
  }, [selectedPeriod, viewMode]);

  const fetchStatistics = async (period) => {
    setLoading(true);
    try {
      const response = await axios.get("/payment-statistics", {
        params: { period },
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });
      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching payment statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllStatistics = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/payment-statistics-all", {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });
      if (response.data.success) {
        setAllStatistics(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching all payment statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = "INR") => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Prepare chart data for currency breakdown
  const getCurrencyChartData = () => {
    if (!statistics?.breakdown?.byCurrency) return [];
    return Object.entries(statistics.breakdown.byCurrency).map(
      ([currency, data]) => ({
        name: currency,
        amount: data.totalAmount,
        count: data.count,
      })
    );
  };

  // Prepare chart data for type breakdown
  const getTypeChartData = () => {
    if (!statistics?.breakdown?.byType) return [];
    return Object.entries(statistics.breakdown.byType).map(
      ([type, data]) => ({
        name: type,
        amount: data.totalAmount,
        count: data.count,
      })
    );
  };

  // Prepare chart data for status breakdown
  const getStatusChartData = () => {
    if (!statistics?.breakdown?.byStatus) return [];
    return Object.entries(statistics.breakdown.byStatus).map(
      ([status, data]) => ({
        name: status,
        amount: data.totalAmount,
        count: data.count,
      })
    );
  };

  // Prepare comparison chart data for all periods
  const getAllPeriodsChartData = () => {
    if (!allStatistics) return [];
    return ["day", "week", "month", "year"].map((period) => ({
      period: period.charAt(0).toUpperCase() + period.slice(1),
      amount: allStatistics[period]?.summary?.totalAmount || 0,
      count: allStatistics[period]?.summary?.paymentCount || 0,
    }));
  };

  const COLORS = ["#0048e6", "#9C15F7", "#FF4BF8", "#28a745", "#ffc107"];

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f8f9fa",
        }}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "primary.main",
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 4,
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 4,
          backgroundColor: "background.paper",
          boxShadow: "0 8px 32px rgba(0,0,0,0.05)",
          maxWidth: 1400,
          mx: "auto",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 4,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                backgroundColor: "background.default",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                fontSize: { xs: "1.5rem", md: "2rem" },
              }}
            >
              Payment Statistics
            </Typography>
          </Box>

          {/* View Mode Toggle */}
          <ButtonGroup variant="outlined" size="medium">
            <Button
              variant={viewMode === "single" ? "contained" : "outlined"}
              onClick={() => setViewMode("single")}
            >
              Single Period
            </Button>
            <Button
              variant={viewMode === "all" ? "contained" : "outlined"}
              onClick={() => setViewMode("all")}
            >
              All Periods
            </Button>
          </ButtonGroup>
        </Box>

        {/* Period Selector (only for single view) */}
        {viewMode === "single" && (
          <Box sx={{ mb: 4 }}>
            <FormControl fullWidth sx={{ maxWidth: 300 }}>
              <InputLabel>Select Period</InputLabel>
              <Select
                value={selectedPeriod}
                label="Select Period"
                onChange={(e) => setSelectedPeriod(e.target.value)}
              >
                <MenuItem value="day">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="year">This Year</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Single Period View */}
        {viewMode === "single" && statistics && (
          <>
            {/* Date Range Info */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Period: {statistics.period.charAt(0).toUpperCase() + statistics.period.slice(1)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(statistics.dateRange.startDate)} -{" "}
                {formatDate(statistics.dateRange.endDate)}
              </Typography>
            </Box>

            {/* Refund summary */}
            {statistics.refundSummary?.refundCount > 0 ? (
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ borderRadius: 3, bgcolor: "#fff7ed" }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Refunded this period
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: "#c2410c" }}>
                        {formatCurrency(statistics.refundSummary.totalRefunded)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {statistics.refundSummary.refundCount} refund(s)
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ borderRadius: 3, bgcolor: "#ecfdf5" }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Autopay verification refunds
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: "#047857" }}>
                        {formatCurrency(statistics.refundSummary.mandateAuth?.totalAmount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {statistics.refundSummary.mandateAuth?.count || 0} refund(s)
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Billing / plan refunds
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        {formatCurrency(statistics.refundSummary.other?.totalAmount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {statistics.refundSummary.other?.count || 0} refund(s)
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : null}

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    background: "linear-gradient(135deg, #0048e6 0%, #9C15F7 100%)",
                    color: "white",
                    borderRadius: 3,
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Total Amount
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {formatCurrency(
                        statistics.summary.totalAmount,
                        statistics.breakdown?.byCurrency
                          ? Object.keys(statistics.breakdown.byCurrency)[0] || "INR"
                          : "INR"
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                    color: "white",
                    borderRadius: 3,
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Payment Count
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {statistics.summary.paymentCount}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    background: "linear-gradient(135deg, #ffc107 0%, #ff9800 100%)",
                    color: "white",
                    borderRadius: 3,
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Average Amount
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {formatCurrency(
                        statistics.summary.averageAmount,
                        statistics.breakdown?.byCurrency
                          ? Object.keys(statistics.breakdown.byCurrency)[0] || "INR"
                          : "INR"
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    background: "linear-gradient(135deg, #dc3545 0%, #e83e8c 100%)",
                    color: "white",
                    borderRadius: 3,
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Total Discount
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {formatCurrency(
                        statistics.summary.totalDiscount,
                        statistics.breakdown?.byCurrency
                          ? Object.keys(statistics.breakdown.byCurrency)[0] || "INR"
                          : "INR"
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Charts Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Currency Breakdown Chart */}
              {getCurrencyChartData().length > 0 && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 3, p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Breakdown by Currency
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={getCurrencyChartData()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip
                          formatter={(value) => formatCurrency(value, "INR")}
                        />
                        <Legend />
                        <Bar dataKey="amount" fill="#0048e6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </Grid>
              )}

              {/* Type Breakdown Chart */}
              {getTypeChartData().length > 0 && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 3, p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Breakdown by Type
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={getTypeChartData()}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) =>
                            `${name}: ${(percent * 100).toFixed(0)}%`
                          }
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="amount"
                        >
                          {getTypeChartData().map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => formatCurrency(value, "INR")}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card>
                </Grid>
              )}

              {/* Status Breakdown Chart */}
              {getStatusChartData().length > 0 && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 3, p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Breakdown by Status
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={getStatusChartData()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip
                          formatter={(value) => formatCurrency(value, "INR")}
                        />
                        <Legend />
                        <Bar dataKey="amount" fill="#28a745" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </Grid>
              )}
            </Grid>

            {/* Breakdown Tables */}
            <Grid container spacing={3}>
              {/* Currency Breakdown Table */}
              {statistics.breakdown?.byCurrency && (
                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        By Currency
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Currency</TableCell>
                              <TableCell align="right">Amount</TableCell>
                              <TableCell align="right">Count</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Object.entries(
                              statistics.breakdown.byCurrency
                            ).map(([currency, data]) => (
                              <TableRow key={currency}>
                                <TableCell>
                                  <Chip label={currency} size="small" />
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(data.totalAmount, currency)}
                                </TableCell>
                                <TableCell align="right">{data.count}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Type Breakdown Table */}
              {statistics.breakdown?.byType && (
                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        By Type
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Type</TableCell>
                              <TableCell align="right">Amount</TableCell>
                              <TableCell align="right">Count</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Object.entries(statistics.breakdown.byType).map(
                              ([type, data]) => (
                                <TableRow key={type}>
                                  <TableCell>
                                    <Chip
                                      label={type}
                                      size="small"
                                      color={
                                        type === "Main" ? "primary" : "secondary"
                                      }
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(data.totalAmount, "INR")}
                                  </TableCell>
                                  <TableCell align="right">
                                    {data.count}
                                  </TableCell>
                                </TableRow>
                              )
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Status Breakdown Table */}
              {statistics.breakdown?.byStatus && (
                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        By Status
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Amount</TableCell>
                              <TableCell align="right">Count</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Object.entries(statistics.breakdown.byStatus).map(
                              ([status, data]) => (
                                <TableRow key={status}>
                                  <TableCell>
                                    <Chip
                                      label={status}
                                      size="small"
                                      color="success"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(data.totalAmount, "INR")}
                                  </TableCell>
                                  <TableCell align="right">
                                    {data.count}
                                  </TableCell>
                                </TableRow>
                              )
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </>
        )}

        {/* All Periods Comparison View */}
        {viewMode === "all" && allStatistics && (
          <>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              Comparison Across All Periods
            </Typography>

            {/* Comparison Chart */}
            {getAllPeriodsChartData().length > 0 && (
              <Card sx={{ borderRadius: 3, p: 2, mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Total Amount by Period
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={getAllPeriodsChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip
                      formatter={(value) => formatCurrency(value, "INR")}
                    />
                    <Legend />
                    <Bar dataKey="amount" fill="#0048e6" name="Total Amount" />
                    <Bar dataKey="count" fill="#9C15F7" name="Payment Count" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}

            {/* Period Cards */}
            <Grid container spacing={3}>
              {["day", "week", "month", "year"].map((period) => {
                const periodData = allStatistics[period];
                if (!periodData) return null;

                return (
                  <Grid item xs={12} sm={6} md={3} key={period}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        background: "linear-gradient(135deg, #0048e6 0%, #9C15F7 100%)",
                        color: "white",
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, textTransform: "capitalize" }}
                        >
                          {period}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                          Total Amount
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                          {formatCurrency(periodData.summary.totalAmount, "INR")}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                          Payment Count
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {periodData.summary.paymentCount}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 2, opacity: 0.8 }}>
                          Avg: {formatCurrency(periodData.summary.averageAmount, "INR")}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}

        {/* No Data Message */}
        {viewMode === "single" && !statistics && !loading && (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              No payment statistics available for the selected period
            </Typography>
          </Box>
        )}

        {viewMode === "all" && !allStatistics && !loading && (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              No payment statistics available
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PaymentStatistics;